import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/delivery.scss";

const deliverySections = [
  ["destinations", "Destinations and timing"],
  ["processing", "Order processing"],
  ["tracking", "Tracking your parcel"],
  ["duties", "Duties and taxes"],
  ["large-items", "Gift orders and special pieces"],
  ["changes", "Address changes"],
  ["issues", "Delays, loss or damage"],
  ["packaging", "Packaging"],
];

function Delivery() {
  const { brandName, contact, currencySymbol, shipping } = siteConfig;

  return (
    <main className="policy-page delivery" aria-labelledby="delivery-title">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Client care / Delivery</p>
        <h1 id="delivery-title">Delivery</h1>
        <div className="policy-page__summary">
          <p>
            From our studio to your door: information about processing,
            shipping, tracking and receiving your {brandName} order.
          </p>
          <time dateTime="2026-08-14">Policy updated 14 August 2026</time>
        </div>
      </header>

      <div className="policy-page__layout">
        <nav className="policy-page__contents" aria-label="Delivery policy contents">
          <p>On this page</p>
          <ol>
            {deliverySections.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="policy-page__content">
          <section id="destinations">
            <h2>Destinations and timing</h2>
            <p>
              Available services, exact charges and a delivery estimate are
              calculated from your destination at checkout. The estimates below
              begin after processing and exclude weekends, public holidays and
              customs delays.
            </p>
            <div className="policy-page__notice">
              Complimentary standard delivery is available in Europe on orders
              over {currencySymbol}{shipping.freeThreshold}.
            </div>
            <div className="policy-page__table-wrap">
              <table>
                <caption>Indicative delivery estimates by destination</caption>
                <thead>
                  <tr>
                    <th scope="col">Destination</th>
                    <th scope="col">Service</th>
                    <th scope="col">Estimate</th>
                    <th scope="col">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">France</th>
                    <td>Tracked standard</td>
                    <td>{shipping.deliveryWindow}</td>
                    <td>At checkout</td>
                  </tr>
                  <tr>
                    <th scope="row">European Union</th>
                    <td>Tracked standard</td>
                    <td>3–7 business days</td>
                    <td>At checkout</td>
                  </tr>
                  <tr>
                    <th scope="row">United Kingdom &amp; Switzerland</th>
                    <td>Tracked international</td>
                    <td>4–8 business days</td>
                    <td>At checkout</td>
                  </tr>
                  <tr>
                    <th scope="row">Rest of world</th>
                    <td>Tracked international</td>
                    <td>5–12 business days</td>
                    <td>At checkout</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              Some countries, remote areas and post-office boxes may not be
              serviceable. Checkout will show the options available for the
              address entered.
            </p>
          </section>

          <section id="processing">
            <h2>Order processing</h2>
            <p>
              In-stock orders are normally prepared within 1–2 business days
              after payment verification. Orders placed after the daily cut-off,
              on weekends or public holidays begin processing the next business
              day.
            </p>
            <p>
              Made-to-order and pre-order pieces have their own lead time on the
              product page and order confirmation. If one order contains pieces
              with different availability dates, they may ship together when
              all are ready unless we agree otherwise.
            </p>
          </section>

          <section id="tracking">
            <h2>Tracking your parcel</h2>
            <p>
              When the parcel leaves us, we send a dispatch email containing a
              carrier link. Tracking can take up to 24 hours to show the first
              scan. A signature may be requested for valuable or oversized
              orders.
            </p>
            <div className="policy-page__notice">
              Please make sure someone can receive the order. Follow the
              carrier’s instructions promptly after a missed attempt to prevent
              the parcel being returned to us.
            </div>
          </section>

          <section id="duties">
            <h2>Duties and taxes</h2>
            <p>
              The checkout indicates whether duties and import taxes are
              included. Where they are not included, the recipient is
              responsible for charges requested by local customs or the carrier.
              These charges are set by the destination country and are outside
              our control.
            </p>
            <p>
              Refusing a parcel because of import charges may result in return
              costs and non-refundable duties being deducted from any refund,
              where permitted by law.
            </p>
          </section>

          <section id="large-items">
            <h2>Gift orders and special pieces</h2>
            <p>
              Cravats are folded with protective interleaving and presented in
              house packaging. If an order is a gift, add the recipient’s
              delivery details at checkout and contact our team promptly if you
              would like a handwritten note added before dispatch.
            </p>
            <p>
              Limited, pre-order and personalised pieces can have a longer lead
              time and may travel separately from in-stock items. Their expected
              dispatch date appears on the product page or order confirmation.
            </p>
          </section>

          <section id="changes">
            <h2>Address changes</h2>
            <p>
              Contact us immediately if an address is incorrect. For security,
              we cannot guarantee changes after payment authorisation or
              dispatch. We may cancel the order and invite you to reorder where
              a material address change cannot be verified.
            </p>
          </section>

          <section id="issues">
            <h2>Delays, loss or damage</h2>
            <p>
              If tracking has not moved for five business days, shows delivery
              when nothing arrived, or your parcel is visibly damaged, contact
              us with the order number. Do not discard packaging or shipping
              labels while a carrier investigation is open.
            </p>
            <p>
              For damage, photograph the parcel before opening and then the
              item, internal packaging and label. See our
              {" "}
              <Link to="/returns">Returns policy</Link> for next steps.
            </p>
          </section>

          <section id="packaging">
            <h2>Packaging</h2>
            <p>
              Orders are packed to protect the piece using the least material
              reasonably possible. Please reuse or recycle outer cartons and
              paper components according to local guidance. Keep all packaging
              until you are certain the piece is right for you.
            </p>
            <div className="policy-page__callout">
              <p>Questions about a destination or delivery</p>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
              <span>{contact.hours}</span>
              <Link to="/contact">Speak with client care ↗</Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
